import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Pin, PinOff, Send, Vote } from "lucide-react";
import { toast } from "sonner";

import { useAuth } from "../contexts/AuthContext";
import { useTeam } from "../contexts/TeamContext";
import { api } from "../lib/chalkApi";
import {
  ChalkCard, ChalkIllustrationLines, DarkHeader, EmptyState,
  WhiteHeader, ChalkActionButton,
} from "../components/chalk/primitives";
import TeamLockBanner from "../components/chalk/TeamLockBanner";
import PollCard from "../components/chalk/PollCard";
import CreatePollSheet from "../components/chalk/CreatePollSheet";
import { startCheckout } from "../lib/checkout";
import { UpgradeMomentSheet } from "../components/chalk/UpgradeMomentSheet";

function humanWhen(iso) {
  const d = new Date(iso);
  return d.toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

export default function ChalkTalk() {
  const { user } = useAuth();
  const { activeTeam } = useTeam();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [pollOpen, setPollOpen] = useState(false);
  const [moment, setMoment] = useState(null);
  const readMarkedRef = useRef(new Set());
  const [nextBefore, setNextBefore] = useState(null);

  async function loadOlder() {
    const data = await api.listMessages(activeTeam.id, nextBefore);
    setMessages((cur) => [...cur, ...(data.messages || []).filter((m) => !cur.some((c) => c.id === m.id))]);
    setNextBefore(data.next_before || null);
  }

  async function load() {
    if (!activeTeam) return;
    try {
      const data = await api.listMessages(activeTeam.id);
      setMessages(data.messages || []);
      setNextBefore(data.next_before || null);
    } catch { /* ignore */ }
  }

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeTeam]);

  // Auto-mark messages as read once when guardian views them
  useEffect(() => {
    if (user.role !== "guardian" || !messages.length) return;
    for (const m of messages) {
      if (m.sender?.id === user.id) continue;
      if (readMarkedRef.current.has(m.id)) continue;
      readMarkedRef.current.add(m.id);
      api.markMessageRead(m.id).catch(() => {});
    }
  }, [messages, user.id, user.role]);

  async function send() {
    if (!activeTeam) {
      toast.error("Create a team first to start posting Chalk Talk.");
      return;
    }
    if (activeTeam.is_locked) {
      startCheckout({
        tier: "chalkboard",
        teamId: activeTeam.id,
        context: { feature_name: "MULTI_TEAM", source_screen: "chalk_talk_send", current_tier: "pickup" },
      }).catch((e) => toast.error(e.message || "Couldn't open checkout."));
      return;
    }
    if (!draft.trim()) return;
    setSending(true);
    try {
      await api.postMessage(activeTeam.id, { body: draft, audience: "everyone", audience_user_ids: [], pinned: false });
      setDraft("");
      toast.success("Chalk Talk sent.");
      load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSending(false);
    }
  }

  async function togglePin(m) {
    try {
      await api.togglePin(m.id, !m.pinned);
      setMessages((arr) => arr.map((x) => (x.id === m.id ? { ...x, pinned: !x.pinned } : x)));
      toast.success(m.pinned ? "Unpinned." : "Pinned to the top.");
    } catch (e) {
      if (e.code === "TIER_INSUFFICIENT") setMoment("second_pin");
      else toast.error(e.message);
    }
  }

  async function openReceipts(m) {
    try {
      const d = await api.messageViewers(m.id);
      if (d.tier_locked) return setMoment("read_receipts");
      const names = (d.viewers || []).map((v) => v.name).join(", ");
      toast.info(names ? `Seen by ${names}` : "No one has seen this yet.");
    } catch (e) { toast.error(e.message); }
  }

  const canPost = user.role !== "guardian";
  const canPin = user.role === "coach" || user.role === "league_admin" || user.role === "assistant";
  const Header = user.role === "guardian" ? WhiteHeader : DarkHeader;
  const sortedMessages = [...messages].sort((a, b) => {
    if (a.pinned !== b.pinned) return a.pinned ? -1 : 1;
    return new Date(b.sent_at) - new Date(a.sent_at);
  });

  return (
    <>
      <Header eyebrow="Chalk Talk" title="Chalk Talk" subtitle={activeTeam ? activeTeam.name : ""} />
      <div style={{ padding: "0 18px 110px" }}>
        <TeamLockBanner team={activeTeam} sourceScreen="chalk_talk" />
        {!activeTeam ? (
          // Brand-new coach with no team yet — point them at the Roster tab
          // where team creation lives. Without this guard the input below
          // would crash with `Cannot read .id of null` on send.
          <EmptyState
            testId="chalktalk-no-team"
            icon={<ChalkIllustrationLines />}
            headline="Create your first team."
            body="Chalk Talk lights up once you have a team. Head to Roster to set one up."
            ctaLabel={canPost ? "Go to Roster" : null}
            onCta={canPost ? () => navigate("/app/roster") : undefined}
          />
        ) : sortedMessages.length === 0 ? (
          <EmptyState
            testId="chalktalk-empty"
            icon={<ChalkIllustrationLines />}
            headline={canPost ? "Your team is listening, Coach." : "Your coach hasn't posted yet."}
            body={canPost ? "Send the first Chalk Talk." : "Updates will land here as your coach posts them."}
          />
        ) : (
          sortedMessages.map((m) => {
            const fromSelf = m.sender && m.sender.id === user.id;
            const isCoach = m.sender && (m.sender.role === "coach" || m.sender.role === "league_admin");
            const isAssistant = m.sender && m.sender.role === "assistant";
            const bubbleBg = isCoach ? "var(--chalk-board)" : isAssistant ? "#1D9E75" : "#fff";
            const bubbleFg = isCoach || isAssistant ? "#F5F0E8" : "var(--chalk-board)";
            const showReceipt = canPost && m.receipts && (isCoach || isAssistant);
            return (
              <div key={m.id} data-testid={`msg-${m.id}`} style={{ marginTop: 12, display: "flex", justifyContent: fromSelf ? "flex-end" : "flex-start" }}>
                <div style={{ maxWidth: "85%" }}>
                  {m.pinned && (
                    <div style={{ fontSize: 10, color: "var(--chalk-amber)", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 4, display: "flex", alignItems: "center", gap: 4, justifyContent: fromSelf ? "flex-end" : "flex-start" }} data-testid={`pinned-${m.id}`}>
                      <Pin size={10} aria-hidden="true" /> Pinned
                    </div>
                  )}
                  <div style={{
                    padding: "10px 14px", borderRadius: 14,
                    background: bubbleBg, color: bubbleFg,
                    border: isCoach || isAssistant ? "none" : "0.5px solid var(--chalk-border)",
                  }}>
                    <div style={{ fontSize: 14, lineHeight: 1.4 }}>{m.body}</div>
                    {m.kind === "poll" && m.poll_id && (
                      <PollCard pollId={m.poll_id} isOwnPost={isCoach || isAssistant} />
                    )}
                    <div style={{ fontSize: 11, color: isCoach || isAssistant ? "rgba(245,240,232,0.65)" : "var(--chalk-muted)", marginTop: 4, display: "flex", justifyContent: "space-between", gap: 8 }}>
                      <span>{m.sender ? `${m.sender.first_name} ${m.sender.last_name[0]}.` : ""} · {humanWhen(m.sent_at)}</span>
                      {showReceipt && (
                        <button type="button" data-testid={`receipt-${m.id}`} onClick={() => openReceipts(m)}
                          style={{ background: "transparent", border: "none", color: "inherit", fontSize: 11, cursor: "pointer", padding: 0, textDecoration: "underline dotted" }}>
                          {m.receipts.reads}/{m.receipts.total_guardians} read
                        </button>
                      )}
                    </div>
                  </div>
                  {canPin && (isCoach || isAssistant) && (
                    <div style={{ display: "flex", justifyContent: fromSelf ? "flex-end" : "flex-start", marginTop: 4 }}>
                      <button
                        onClick={() => togglePin(m)}
                        data-testid={`pin-toggle-${m.id}`}
                        aria-label={m.pinned ? "Unpin" : "Pin"}
                        style={{ background: "transparent", border: "none", color: "var(--chalk-muted)", fontSize: 11, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4 }}
                      >
                        {m.pinned ? <PinOff size={12} aria-hidden="true" /> : <Pin size={12} aria-hidden="true" />}
                        {m.pinned ? "Unpin" : "Pin"}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
        {nextBefore && (
          <button type="button" data-testid="chalktalk-load-older" onClick={() => loadOlder().catch(() => {})}
            style={{ display: "block", margin: "12px auto 0", background: "transparent", border: "1px solid var(--chalk-border)",
              borderRadius: 999, padding: "8px 18px", color: "var(--chalk-board)", fontSize: 13, cursor: "pointer" }}>
            Load older messages
          </button>
        )}
      </div>
      {canPost && activeTeam && (
        <div style={{ position: "fixed", bottom: 80, left: 0, right: 0, padding: "0 18px", maxWidth: 480, margin: "0 auto" }}>
          <ChalkCard style={{ display: "flex", gap: 8, alignItems: "center", padding: 8 }}>
            {/* Poll composer launcher. Wired to the V1.5 polls backend
                — coaches and assistants can post Pick-one / Pick-many /
                Yes-No polls. Hidden for guardians (canPost gate above). */}
            <button
              type="button"
              data-testid="chalktalk-new-poll"
              onClick={() => {
                if (activeTeam.is_locked) {
                  startCheckout({
                    tier: "chalkboard",
                    teamId: activeTeam.id,
                    context: { feature_name: "MULTI_TEAM", source_screen: "chalk_talk_poll", current_tier: "pickup" },
                  }).catch((e) => toast.error(e.message || "Couldn't open checkout."));
                  return;
                }
                setPollOpen(true);
              }}
              aria-label="Create a poll"
              style={{
                width: 38, height: 38, borderRadius: 10,
                background: "transparent", border: "0.5px solid var(--chalk-border)",
                color: "var(--green-mid)", cursor: "pointer",
                display: "inline-flex", alignItems: "center", justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <Vote size={18} aria-hidden="true" />
            </button>
            <input
              data-testid="chalktalk-input"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="Write a quick Chalk Talk…"
              className="chalk-input"
              style={{ paddingLeft: 14, border: "none", boxShadow: "none" }}
              onKeyDown={(e) => e.key === "Enter" && send()}
            />
            <ChalkActionButton testId="chalktalk-send" variant="primary" label="Send" loading={sending} fullWidth={false} icon={<Send size={16} aria-hidden="true" />} onClick={send} />
          </ChalkCard>
        </div>
      )}
      <CreatePollSheet
        isOpen={pollOpen}
        teamId={activeTeam?.id}
        onDismiss={() => setPollOpen(false)}
        onCreated={() => { setPollOpen(false); load(); }}
      />
      <UpgradeMomentSheet gate={moment} onClose={() => setMoment(null)} context={{ source_screen: "chalk_talk", team_id: activeTeam?.id }} />
    </>
  );
}
